import {
  Ban,
  Banknote,
  Bell,
  KeyRound,
  LoaderCircle,
  Lock,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ConfirmationModal from "../../components/ConfirmationModal";
import ContractExclusionsCard from "../../components/ContractExclusionsCard";
import { ClaudeConnectedAppsCard } from "../../components/claudeConnector/ClaudeConnectorPlacements";
import { useClaudeConnectorEnabled } from "../../hooks/useClaudeConnectorEnabled";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import {
  getPendingAccountAccessDecisionCount,
  isSelfAccountAccessGrant,
  useActorAccountAccessGrants,
} from "../../hooks/useActorAccountAccessGrants";
import accountAccessGrantsService, {
  type AccountAccessGrantTransitionInput,
  type ActorAccountAccessGrant,
} from "../../services/accountAccessGrantsService";
import contractsService, { type ContractDetails } from "../../services/contractsService";
import profileConfigService, { type UserSettingsMap } from "../../services/profileConfigService";
import profileExclusionsService, { type ContractExclusion } from "../../services/profileExclusionsService";
import subscriptionService from "../../services/subscriptionService";
import {
  getInvestorAccessSummary,
  normalizeSubscriptionSettings,
  type SubscriptionWithRelations,
} from "../../types/subscription";
import {
  getCashDeploymentSubtext,
  getCashDeploymentSummary,
  resolveCashDeploymentMode,
} from "../../utils/cashDeploymentSettings";
import { matchesWidget } from "../../utils/settingsWidgetSearch";
import notificationSettingsService from "../../services/notificationSettingsService";
import AutomatedRebalancingExplainerModal from "../dashboard/components/AutomatedRebalancingExplainerModal";
import CashDeploymentExplainerModal from "../dashboard/components/CashDeploymentExplainerModal";
import CashDeploymentSettingPanel from "../dashboard/components/CashDeploymentSettingPanel";
import ContractExclusionsExplainerModal from "../dashboard/components/ContractExclusionsExplainerModal";
import AlphaPreferencesCard from "./AlphaPreferencesCard";
import AlphaAccountAccessSection from "./AlphaAccountAccessSection";
import AlphaSettingWidget from "./AlphaSettingWidget";
import AlphaSettingsModal from "./AlphaSettingsModal";
import { openAlphaRiskProfileUpdate } from "./alphaHistory";
import type { AlphaProfilePageProps } from "./alphaPageSubject";
import AccountDeletionSetting from "./AccountDeletionSetting";
import AlphaYourDocumentsSetting from "./AlphaYourDocumentsSetting";
import AdminTargetProfileAdapter from "./AdminTargetProfileAdapter";
import { getAlphaSetupDestination } from "./alphaSetupDestination";

interface EnrichedExclusion extends ContractExclusion {
  symbol?: string;
  longName?: string;
}

interface AlphaSettingWidgetConfig {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  searchText: string;
  node: React.ReactNode;
}

const AlphaProviderAvatar: React.FC<{
  providerId: number;
  name: string;
  logoUrl?: string | null;
}> = ({ providerId, name, logoUrl }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const src = logoUrl || `/provider-assets/${providerId}/logo.png`;

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-v7-control border border-v7-ink/15 bg-v7-paper">
      {!imageFailed ? (
        <img
          src={src}
          alt={name}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="text-sm font-bold text-v7-ink">
          {name.slice(0, 1).toUpperCase()}
        </span>
      )}
    </div>
  );
};

const AlphaAccessBadge: React.FC<{ ids: number[] }> = ({ ids }) => {
  const label = getInvestorAccessSummary(ids);
  const className = ids.length === 0
    ? "border-v7-success/30 bg-v7-success/10 text-v7-success"
    : ids.length === 1 && ids[0] === -1
      ? "border-v7-ink/15 bg-v7-ink/[0.04] text-v7-ink-soft"
      : "border-v7-bronze/30 bg-v7-bronze/10 text-v7-bronze-dark";

  return (
    <span className={`rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${className}`}>
      {label}
    </span>
  );
};

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function SelfProfileAdapter({
  progressResponses,
  isPersistenceLoading = false,
  persistenceLoadFailed = false,
  onRetryPersistence,
  isReviewCompleted = false,
  isBrokerageProvisioned = false,
}: Extract<AlphaProfilePageProps, { mode: "self" }>): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { success: showSuccessToast, error: showErrorToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedSection = searchParams.get("section");
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);
  const [notificationEmail, setNotificationEmail] = useState<{ loading: boolean; error: string | null; value: string | null }>({
    loading: false,
    error: null,
    value: null,
  });
  const notificationEmailReqRef = useRef(0);
  useEffect(() => {
    const reqId = ++notificationEmailReqRef.current;
    setNotificationEmail({ loading: true, error: null, value: null });
    (async () => {
      try {
        const endpoints = await notificationSettingsService.getEndpoints();
        if (reqId !== notificationEmailReqRef.current) return;
        // "(unverified)" only when verification is required and the address has not been confirmed (PWA-2698).
        const unverifiedSuffix =
          endpoints.email && !endpoints.email.verifiedAtUtc && endpoints.verification.email.required ? " (unverified)" : "";
        setNotificationEmail({
          loading: false,
          error: null,
          value: endpoints.email ? `${endpoints.email.value}${unverifiedSuffix}` : null,
        });
      } catch {
        if (reqId !== notificationEmailReqRef.current) return;
        setNotificationEmail({ loading: false, error: "Could not load notification settings.", value: null });
      }
    })();
  }, []);
  const {
    getUserConfigValue,
    user,
    userConfig,
    userConfigLoaded,
    refreshUserConfig,
    effectiveUserId,
    refreshAccounts,
    runInActorAccountContext,
  } = useAuth();
  const claudeConnectorEnabled = useClaudeConnectorEnabled();
  const accountAccessGrantsQuery = useActorAccountAccessGrants();
  const accountAccessGrants = accountAccessGrantsQuery.data ?? [];
  const visibleAccountAccessGrants = useMemo(
    () => accountAccessGrants.filter((grant) => !isSelfAccountAccessGrant(grant)),
    [accountAccessGrants]
  );
  const pendingAccountAccessCount = getPendingAccountAccessDecisionCount(
    visibleAccountAccessGrants
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [exclusions, setExclusions] = useState<EnrichedExclusion[]>([]);
  const [exclusionsLoading, setExclusionsLoading] = useState(false);
  const [exclusionsError, setExclusionsError] = useState<string | null>(null);
  const [exclusionsModalOpen, setExclusionsModalOpen] = useState(false);

  const [subscriptions, setSubscriptions] = useState<SubscriptionWithRelations[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(null);
  const [subscriptionsModalOpen, setSubscriptionsModalOpen] = useState(false);
  const [accountAccessModalOpen, setAccountAccessModalOpen] = useState(false);

  const [tradingConfirmOpen, setTradingConfirmOpen] = useState(false);
  const [tradingLoading, setTradingLoading] = useState(false);
  const [tradingError, setTradingError] = useState<string | null>(null);

  const [cashDeploymentModalOpen, setCashDeploymentModalOpen] = useState(false);
  const [cashDeploymentExplainerOpen, setCashDeploymentExplainerOpen] = useState(false);
  const [rebalancingExplainerOpen, setRebalancingExplainerOpen] = useState(false);
  const [exclusionsExplainerOpen, setExclusionsExplainerOpen] = useState(false);

  // Request tokens guarding async state writes against out-of-order
  // resolution: a slower earlier request must never overwrite a newer one.
  const exclusionsReqRef = useRef(0);
  const subscriptionsReqRef = useRef(0);

  const isCustomer = getUserConfigValue("iscustomer") === "true";
  // Feature flag: cash deployment is gated on the per-user feature_cash_deploy_mode config flag.
  const isCashDeploymentVisible = getUserConfigValue("feature_cash_deploy_mode") === "true";
  const userRoles = useMemo(() => {
    const roles = user?.roles || [];
    const roleRecords = user?.userRoles?.map((userRole) => userRole.role) || [];
    return Array.from(new Set([...roleRecords, ...roles]));
  }, [user]);
  const isInvestor = userRoles.includes("INVESTOR") || userRoles.length === 0;
  const adminTradingEnabled = getUserConfigValue("tradingenabled") === "true";
  const userSettingsRaw = userConfig?.data?.userSettings;
  const userSettings = userSettingsRaw && typeof userSettingsRaw === "object"
    ? userSettingsRaw as UserSettingsMap
    : undefined;
  const userTradingEnabled = userSettings?.user_tradingenabled !== false;
  const accountTrading = adminTradingEnabled && userTradingEnabled;
  const cashDeploymentMode = resolveCashDeploymentMode(userSettings);
  const cashDeploymentSummary = getCashDeploymentSummary(cashDeploymentMode);
  const cashDeploymentSubtext = getCashDeploymentSubtext(cashDeploymentMode);
  const rebalancingStatus = useMemo(() => {
    if (!userConfigLoaded) return undefined;
    if (accountTrading) {
      return { label: "Trading active", tone: "success" as const, pulse: true };
    }
    if (userTradingEnabled) {
      return { label: "System pending", tone: "warning" as const };
    }
    return { label: "Rebalancing paused", tone: "danger" as const };
  }, [accountTrading, userConfigLoaded, userTradingEnabled]);

  const fetchExclusions = useCallback(async () => {
    const reqId = ++exclusionsReqRef.current;
    setExclusionsLoading(true);
    setExclusionsError(null);
    try {
      const response = await profileExclusionsService.getExclusions();
      const rawExclusions = response.exclusions || [];

      if (rawExclusions.length === 0) {
        if (reqId !== exclusionsReqRef.current) return;
        setExclusions([]);
        return;
      }

      const conIds = rawExclusions.map((exclusion) => exclusion.conId);
      let contractMap: Record<number, ContractDetails> = {};
      try {
        contractMap = await contractsService.getContractDetailsMap(conIds);
      } catch (err) {
        console.warn("Failed to resolve contract details for profile settings summary:", err);
      }

      if (reqId !== exclusionsReqRef.current) return;
      setExclusions(rawExclusions.map((exclusion) => ({
        ...exclusion,
        symbol: contractMap[exclusion.conId]?.symbol,
        longName: contractMap[exclusion.conId]?.longName,
      })));
    } catch (err: any) {
      if (reqId !== exclusionsReqRef.current) return;
      const status = err.response?.status || err.status;
      if (status === 404) {
        setExclusions([]);
      } else {
        setExclusionsError(err.response?.data?.error || err.message || "Failed to load exclusions");
      }
    } finally {
      if (reqId === exclusionsReqRef.current) {
        setExclusionsLoading(false);
      }
    }
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    const reqId = ++subscriptionsReqRef.current;
    if (!isInvestor) {
      setSubscriptions([]);
      setSubscriptionsError(null);
      setSubscriptionsLoading(false);
      return;
    }

    setSubscriptionsLoading(true);
    setSubscriptionsError(null);
    try {
      const data = await subscriptionService.getInvestorSubscriptions();
      if (reqId !== subscriptionsReqRef.current) return;
      setSubscriptions(data);
    } catch (err: any) {
      if (reqId !== subscriptionsReqRef.current) return;
      setSubscriptionsError(err.response?.data?.error || err.message || "Failed to load subscriptions");
    } finally {
      if (reqId === subscriptionsReqRef.current) {
        setSubscriptionsLoading(false);
      }
    }
  }, [isInvestor]);

  useEffect(() => {
    fetchExclusions();
    fetchSubscriptions();
  }, [fetchExclusions, fetchSubscriptions]);

  useEffect(() => {
    refreshUserConfig().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exclusionPreview = useMemo(
    () => exclusions.slice(0, 3).map((exclusion) => exclusion.symbol || `ConId ${exclusion.conId}`),
    [exclusions]
  );

  const subscriptionsSearchText = useMemo(
    () => subscriptions.map((subscription) => {
      const settings = normalizeSubscriptionSettings(subscription.subscriptionSettings);
      return [
        subscription.provider.name,
        getInvestorAccessSummary(settings.portfolios),
        getInvestorAccessSummary(settings.baskets),
        settings.isSubscribedExternallyToProvider ? "external" : "in-app strategy sync organic",
      ].join(" ");
    }).join(" "),
    [subscriptions]
  );

  const accountAccessSearchText = useMemo(
    () => visibleAccountAccessGrants.map((grant) => [
      grant.actorRelationship,
      grant.status,
      grant.approvalMode,
      grant.granteeUser.name,
      grant.granteeUser.alias,
      grant.granteeUser.email,
      grant.targetUser.name,
      grant.targetUser.alias,
      grant.targetUser.email,
    ].filter(Boolean).join(" ")).join(" "),
    [visibleAccountAccessGrants]
  );

  const handleTradingToggle = async () => {
    setTradingLoading(true);
    setTradingError(null);
    try {
      await profileConfigService.patchUserSettings({
        user_tradingenabled: !userTradingEnabled,
      });
      await refreshUserConfig();
      setTradingConfirmOpen(false);
    } catch {
      setTradingError("Failed to update rebalancing setting.");
    } finally {
      setTradingLoading(false);
    }
  };

  const openCashDeploymentModal = useCallback(() => {
    setCashDeploymentModalOpen(true);
  }, []);

  const handleCashDeploymentSaved = useCallback(async () => {
    await refreshUserConfig();
    setCashDeploymentModalOpen(false);
  }, [refreshUserConfig]);

  const handleAccountAccessRevoke = useCallback(async (
    grant: ActorAccountAccessGrant,
    input: AccountAccessGrantTransitionInput
  ): Promise<void> => {
    const revoke = async (): Promise<void> => {
      await accountAccessGrantsService.revokeGrant(grant.grantId, input);
    };
    const isRelinquishingCurrentAccount = grant.actorRelationship === "GRANTEE"
      && grant.targetUser.userId === effectiveUserId;

    if (isRelinquishingCurrentAccount) {
      await runInActorAccountContext(revoke);
      return;
    }
    await revoke();
  }, [effectiveUserId, runInActorAccountContext]);

  const handleAccountAccessFeedback = useCallback((
    tone: "success" | "error",
    message: string
  ): void => {
    if (tone === "success") {
      showSuccessToast(message);
    } else {
      showErrorToast(message);
    }
  }, [showErrorToast, showSuccessToast]);

  const clearSectionFilter = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Changing the answers means retaking the preference and risk questions.
  // Profile-update mode returns here on save/back and never enters the setup
  // flow (no review, no agreement, no brokerage).
  const openRiskQuestions = useCallback(() => {
    openAlphaRiskProfileUpdate(navigate, location.state, "/alpha/profile");
  }, [navigate, location.state]);

  const startSetup = useCallback(() => {
    const destination = progressResponses
      ? getAlphaSetupDestination(progressResponses, {
        hasCompletedReview: isReviewCompleted,
        isBrokerageProvisioned,
      })
      : null;
    navigate(destination?.path ?? "/alpha", { state: location.state });
  }, [progressResponses, isReviewCompleted, isBrokerageProvisioned, navigate, location.state]);

  const preferencesNode = useMemo(() => isPersistenceLoading ? (
    <div
      role="status"
      className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 rounded-v7-panel border border-v7-ink/15 bg-v7-white p-5 text-sm text-v7-ink-soft"
    >
      <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
      Loading your profile
    </div>
  ) : persistenceLoadFailed ? (
    <div
      role="alert"
      className="flex h-full min-h-[280px] flex-col justify-center gap-4 rounded-v7-panel border border-red-200 bg-red-50 p-5 text-sm text-red-700"
    >
      <p>Your profile is temporarily unavailable.</p>
      {onRetryPersistence && (
        <button
          type="button"
          onClick={onRetryPersistence}
          className="inline-flex min-h-10 w-max items-center gap-2 rounded-v7-control border border-red-300 bg-v7-white px-3 font-medium text-red-700 transition-colors hover:bg-red-100"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Retry profile
        </button>
      )}
    </div>
  ) : (
    <AlphaPreferencesCard
      variant="tile"
      progressResponses={progressResponses}
      onUpdate={openRiskQuestions}
      onStartSetup={startSetup}
    />
  ), [
    isPersistenceLoading,
    persistenceLoadFailed,
    onRetryPersistence,
    progressResponses,
    openRiskQuestions,
    startSetup,
  ]);

  const widgetConfigs: AlphaSettingWidgetConfig[] = useMemo(() => {
    const configs: AlphaSettingWidgetConfig[] = [
      {
        id: "portfolio-preferences",
        title: "Portfolio preferences",
        description: "Objectives and risk profile",
        keywords: ["objective", "objectives", "risk", "horizon", "preferences", "profile", "experience"],
        searchText: "",
        node: preferencesNode,
      },
      {
        id: "account-access",
        title: "Account access",
        description: "Review access requests and shared account relationships.",
        keywords: [
          "account",
          "access",
          "request",
          "approve",
          "reject",
          "revoke",
          "relinquish",
          "shared",
        ],
        searchText: accountAccessSearchText,
        node: (
          <AlphaSettingWidget
            icon={<KeyRound className="h-5 w-5" />}
            title="Account access"
            description="Review access requests and shared account relationships."
            summary={accountAccessGrantsQuery.isLoading
              ? "Loading..."
              : accountAccessGrantsQuery.isError
                ? "Unavailable"
                : pendingAccountAccessCount > 0
                  ? `${pendingAccountAccessCount} pending ${pendingAccountAccessCount === 1 ? "request" : "requests"}`
                  : visibleAccountAccessGrants.length === 0
                    ? "No shared access"
                    : `${visibleAccountAccessGrants.length} ${visibleAccountAccessGrants.length === 1 ? "relationship" : "relationships"}`}
            subtext={pendingAccountAccessCount > 0
              ? "Review requests that need your decision."
              : "See who can access your account and which accounts are shared with you."}
            badge={pendingAccountAccessCount > 0 ? "Action needed" : undefined}
            isLoading={accountAccessGrantsQuery.isLoading}
            error={accountAccessGrantsQuery.isError
              ? "Account access is temporarily unavailable."
              : undefined}
            actionLabel="Review"
            onAction={() => setAccountAccessModalOpen(true)}
          />
        ),
      },
      {
        id: "notifications",
        title: "Notifications",
        description: "Choose how Plutus reaches you.",
        keywords: ["notifications", "email", "alerts", "inbox", "preferences"],
        searchText: notificationEmail.value ?? "",
        node: (
          <AlphaSettingWidget
            icon={<Bell className="h-5 w-5" />}
            title="Notifications"
            description="Choose how Plutus reaches you."
            summary={notificationEmail.value ? `Email: ${notificationEmail.value}` : "No notification email set"}
            subtext="Inbox, channel preferences and notification email."
            isLoading={notificationEmail.loading}
            error={notificationEmail.error}
            onAction={() => navigate("/alpha/notifications?tab=preferences", { state: location.state })}
          />
        ),
      },
      {
        id: "contract-exclusions",
        title: "Contract Exclusions",
        description: "Block stocks from automated trading and rebalancing.",
        keywords: ["stocks", "blocked", "excluded", "contracts", "rebalance", "trading"],
        searchText: exclusionPreview.join(" "),
        node: (
          <AlphaSettingWidget
            icon={<Ban className="h-5 w-5" />}
            title="Contract Exclusions"
            description="Block stocks from automated trading and rebalancing."
            summary={
              exclusions.length === 0
                ? "No stocks excluded"
                : `${exclusions.length} excluded ${exclusions.length === 1 ? "stock" : "stocks"}`
            }
            subtext={
              exclusionPreview.length > 0
                ? exclusionPreview.join(", ")
                : "Use this when a stock should never be traded for your account."
            }
            isLoading={exclusionsLoading}
            error={exclusionsError}
            helpGuide={{
              triggerLabel: "Learn",
              title: "How contract exclusions work",
              content: "Walk through a simple rebalance example and see what happens when a stock is excluded.",
              clickHint: "Click to open animated example",
              triggerVariant: "callout",
              placement: "bottom-right",
              onClick: () => setExclusionsExplainerOpen(true),
            }}
            onAction={() => setExclusionsModalOpen(true)}
          />
        ),
      },
    ];

    if (isInvestor) {
      configs.push({
        id: "subscriptions",
        title: "Provider Subscriptions",
        description: "View provider access levels for baskets and portfolios.",
        keywords: ["provider", "subscriptions", "subscriber", "portfolio", "basket", "access"],
        searchText: subscriptionsSearchText,
        node: (
          <AlphaSettingWidget
            icon={<Lock className="h-5 w-5" />}
            title="Provider Subscriptions"
            description="View provider access levels for baskets and portfolios."
            summary={
              subscriptions.length === 0
                ? "No subscriptions"
                : `${subscriptions.length} subscribed ${subscriptions.length === 1 ? "provider" : "providers"}`
            }
            subtext={
              subscriptions.length > 0
                ? subscriptions.slice(0, 3).map((s) => s.provider.name).join(", ")
                : "Subscriber-only products appear here when access is granted."
            }
            isLoading={subscriptionsLoading}
            error={subscriptionsError}
            actionLabel="View"
            onAction={() => setSubscriptionsModalOpen(true)}
          />
        ),
      });
    }

    configs.push({
      id: "your-documents",
      title: "Your documents",
      description: "Signed agreements for your account.",
      keywords: ["documents", "agreement", "advisory", "signed", "pdf", "download", "contract"],
      searchText: "",
      node: <AlphaYourDocumentsSetting key={user?.id} />,
    });

    if ((!userConfigLoaded || isCustomer) && isCashDeploymentVisible) {
      configs.splice(2, 0, {
        id: "cash-deployment",
        title: "New Cash Deployment",
        description: "Choose how new cash is handled in your strategy.",
        keywords: ["cash", "deploy", "deployment", "reserve", "auto-deploy", "strategy", "allocation"],
        searchText: `${cashDeploymentSummary} ${cashDeploymentSubtext}`,
        node: (
          <AlphaSettingWidget
            icon={<Banknote className="h-5 w-5" />}
            title="New Cash Deployment"
            description="Choose how new cash is handled in your strategy."
            summary={!userConfigLoaded ? "Loading..." : cashDeploymentSummary}
            subtext={!userConfigLoaded ? undefined : cashDeploymentSubtext}
            badge={cashDeploymentMode === null ? "Default" : undefined}
            isLoading={!userConfigLoaded}
            actionLabel="Manage"
            helpGuide={{
              triggerLabel: "Learn",
              title: "How cash deployment works",
              content: "Walk through a simple deposit example and compare auto-deploy vs reserve cash step by step.",
              clickHint: "Click to open animated example",
              triggerVariant: "callout",
              placement: "bottom-right",
              onClick: () => setCashDeploymentExplainerOpen(true),
            }}
            onAction={userConfigLoaded ? openCashDeploymentModal : undefined}
          />
        ),
      });
    }

    if (!userConfigLoaded || isCustomer) {
      configs.splice(isCashDeploymentVisible ? 3 : 2, 0, {
        id: "automated-rebalancing",
        title: "Automated Rebalancing",
        description: "Pause or resume user-level automated trading.",
        keywords: ["trading", "pause", "resume", "automation", "rebalance", "rebalancing"],
        searchText: accountTrading
          ? "active system ready"
          : userTradingEnabled
            ? "inactive system pending"
            : "paused stopped disabled",
        node: (
          <AlphaSettingWidget
            icon={userTradingEnabled ? <PauseCircle className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
            title="Automated Rebalancing"
            description="Pause or resume user-level automated trading."
            summary={
              !userConfigLoaded
                ? "Loading..."
                : accountTrading
                  ? "Trading active"
                  : userTradingEnabled
                    ? "System pending"
                    : "Rebalancing paused"
            }
            subtext={
              adminTradingEnabled
                ? "System trading is ready. Your user-level setting controls automation."
                : "System trading is not enabled for this account."
            }
            badge={accountTrading ? "Active" : "Inactive"}
            status={rebalancingStatus}
            isLoading={!userConfigLoaded}
            error={tradingError}
            actionLabel={userTradingEnabled ? "Pause" : "Resume"}
            actionDestructive={userTradingEnabled}
            helpGuide={{
              triggerLabel: "Learn",
              title: "How automated rebalancing works",
              content: "Walk through a drift example and compare trading active versus rebalancing paused.",
              clickHint: "Click to open animated example",
              triggerVariant: "callout",
              placement: "bottom-right",
              onClick: () => setRebalancingExplainerOpen(true),
            }}
            onAction={userConfigLoaded ? () => setTradingConfirmOpen(true) : undefined}
          />
        ),
      });
    }

    configs.push({
      id: "delete-account",
      title: "Delete account",
      description: "Delete your personal account.",
      keywords: ["delete", "close", "remove", "account"],
      searchText: "",
      node: <AccountDeletionSetting key={user?.id} />,
    });
    if (claudeConnectorEnabled) {
      configs.unshift({
        id: "connected-apps",
        title: "Connected apps",
        description: "Choose your AI connections.",
        keywords: ["claude", "chatgpt", "ai", "connect", "connector", "report", "reporting", "read-only"],
        searchText: "",
        node: <ClaudeConnectedAppsCard />,
      });
    }
    return configs;
  }, [
    claudeConnectorEnabled,
    user?.id,
    accountAccessGrantsQuery.isError,
    accountAccessGrantsQuery.isLoading,
    accountAccessSearchText,
    accountTrading,
    adminTradingEnabled,
    cashDeploymentMode,
    cashDeploymentSubtext,
    cashDeploymentSummary,
    exclusionPreview,
    exclusions.length,
    exclusionsError,
    exclusionsLoading,
    isCashDeploymentVisible,
    isCustomer,
    isInvestor,
    location.state,
    location.search,
    navigate,
    notificationEmail,
    openCashDeploymentModal,
    preferencesNode,
    pendingAccountAccessCount,
    rebalancingStatus,
    subscriptions,
    subscriptionsError,
    subscriptionsLoading,
    subscriptionsSearchText,
    tradingError,
    userConfigLoaded,
    userTradingEnabled,
    visibleAccountAccessGrants.length,
  ]);

  const focusedWidget = useMemo(
    () => (focusedSection ? widgetConfigs.find((widget) => widget.id === focusedSection) : undefined),
    [focusedSection, widgetConfigs]
  );

  const sectionFilteredWidgets = useMemo(() => {
    if (!focusedSection) return widgetConfigs;
    return focusedWidget ? [focusedWidget] : [];
  }, [focusedSection, focusedWidget, widgetConfigs]);

  const visibleWidgets = sectionFilteredWidgets.filter((widget) => matchesWidget(widget, searchTerm));

  useEffect(() => {
    if (!focusedSection || !userConfigLoaded || !focusedWidget) return;

    const timer = window.setTimeout(() => {
      const target = document.getElementById(`setting-${focusedSection}`);
      if (!target) return;
      if (typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setHighlightedSection(focusedSection);
      window.setTimeout(() => setHighlightedSection(null), 2000);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [focusedSection, focusedWidget, userConfigLoaded]);

  return (
    <main
      data-testid="alpha-profile-content"
      className="mx-auto min-h-full w-full max-w-[1400px] px-4 pb-28 pt-7 md:px-10 md:py-10"
    >
      <div className="space-y-6">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-v7-ink/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search settings..."
            className="w-full rounded-v7-control border border-v7-ink/20 bg-v7-white py-2.5 pl-10 pr-10 text-sm text-v7-ink outline-none transition-colors placeholder:text-v7-ink/40 focus:border-v7-bronze-dark focus:ring-1 focus:ring-v7-bronze-dark"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-v7-ink/40 transition-colors hover:text-v7-ink"
              aria-label="Clear settings search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {focusedSection && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-v7-control border border-v7-bronze/40 bg-v7-bronze/10 px-4 py-3">
            <p className="text-sm text-v7-ink-soft">
              Showing{" "}
              <span className="font-semibold text-v7-ink">
                {focusedWidget?.title ?? focusedSection}
              </span>
            </p>
            <button
              type="button"
              onClick={clearSectionFilter}
              className="rounded-v7-control border border-v7-ink/25 bg-v7-white px-3 py-1.5 text-sm font-medium text-v7-ink transition-colors hover:border-v7-ink"
            >
              Show all settings
            </button>
          </div>
        )}

        {visibleWidgets.length === 0 ? (
          <div className="rounded-v7-panel border border-v7-ink/15 bg-v7-white py-24 text-center">
            <SlidersHorizontal className="mx-auto mb-3 h-8 w-8 text-v7-ink/30" />
            <p className="text-sm text-v7-ink-soft">
              {focusedSection && !focusedWidget
                ? "This setting is not available for your account."
                : searchTerm
                  ? `No settings match "${searchTerm}".`
                  : "No settings found."}
            </p>
            {focusedSection && (
              <button
                type="button"
                onClick={clearSectionFilter}
                className="mt-4 rounded-v7-control bg-v7-ink px-4 py-2 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft"
              >
                Show all settings
              </button>
            )}
          </div>
        ) : (
          <div className="grid auto-rows-fr grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {visibleWidgets.map((widget) => (
              <div
                key={widget.id}
                id={`setting-${widget.id}`}
                className={
                  highlightedSection === widget.id
                    ? "h-full rounded-v7-panel ring-2 ring-v7-bronze ring-offset-2 ring-offset-v7-canvas transition-shadow"
                    : "h-full"
                }
              >
                {widget.node}
              </div>
            ))}
          </div>
        )}
      </div>

      <AlphaSettingsModal
        isOpen={exclusionsModalOpen}
        onClose={() => {
          setExclusionsModalOpen(false);
          fetchExclusions();
        }}
        title="Manage Contract Exclusions"
        maxWidthClass="max-w-5xl"
      >
        <ContractExclusionsCard onExclusionsChanged={fetchExclusions} appearance="alpha" />
      </AlphaSettingsModal>

      <AlphaSettingsModal
        isOpen={subscriptionsModalOpen}
        onClose={() => setSubscriptionsModalOpen(false)}
        title="Provider Subscriptions"
        maxWidthClass="max-w-5xl"
      >
        {subscriptionsLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((item) => (
              <div
                key={item}
                className="h-20 animate-pulse rounded-v7-panel border border-v7-ink/15 bg-v7-mist"
              />
            ))}
          </div>
        ) : subscriptionsError ? (
          <div className="rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-3 text-sm text-v7-rust">
            {subscriptionsError}
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="rounded-v7-panel border border-v7-ink/15 bg-v7-white py-16 text-center">
            <ShieldCheck className="mx-auto mb-3 h-8 w-8 text-v7-ink/30" />
            <p className="text-sm text-v7-ink-soft">
              No provider subscriptions yet.
            </p>
            <button
              type="button"
              onClick={() => navigate("/alpha/discover", { state: location.state })}
              className="mt-4 rounded-v7-control bg-v7-ink px-4 py-2 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft"
            >
              Discover Products
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-v7-panel border border-v7-ink/15">
            <div className="divide-y divide-v7-ink/10">
              {subscriptions.map((subscription) => {
                const settings = normalizeSubscriptionSettings(subscription.subscriptionSettings);
                const sourceLabel = settings.isSubscribedExternallyToProvider
                  ? "External"
                  : "In-app or strategy sync";

                return (
                  <div
                    key={subscription.id}
                    className="bg-v7-white p-4 transition-colors hover:bg-v7-paper"
                  >
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-start gap-3">
                        <AlphaProviderAvatar
                          providerId={subscription.providerId}
                          name={subscription.provider.name}
                          logoUrl={subscription.provider.logoUrl}
                        />
                        <div>
                          <h3 className="text-base font-bold leading-tight text-v7-ink">
                            {subscription.provider.name}
                          </h3>
                          <p className="mt-1 text-xs text-v7-ink/60">
                            Created {formatDate(subscription.createdAtUtc)} - Updated {formatDate(subscription.updatedAtUtc)}
                          </p>
                          <span className="mt-2 inline-flex rounded border border-v7-ink/15 bg-v7-ink/[0.04] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-v7-ink-soft">
                            {sourceLabel}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:min-w-[360px]">
                        <div className="rounded-v7-control border border-v7-ink/15 bg-v7-paper p-3">
                          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-v7-ink/55">
                            Portfolios
                          </p>
                          <AlphaAccessBadge ids={settings.portfolios} />
                        </div>
                        <div className="rounded-v7-control border border-v7-ink/15 bg-v7-paper p-3">
                          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-v7-ink/55">
                            Baskets
                          </p>
                          <AlphaAccessBadge ids={settings.baskets} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </AlphaSettingsModal>

      <AlphaSettingsModal
        isOpen={accountAccessModalOpen}
        onClose={() => setAccountAccessModalOpen(false)}
        title="Account access"
        maxWidthClass="max-w-5xl"
      >
        <AlphaAccountAccessSection
          revokeGrant={handleAccountAccessRevoke}
          onAccountContextsChanged={refreshAccounts}
          onPersistentFeedback={handleAccountAccessFeedback}
        />
      </AlphaSettingsModal>

      <AlphaSettingsModal
        isOpen={cashDeploymentModalOpen}
        onClose={() => setCashDeploymentModalOpen(false)}
        title="New Cash Deployment"
        maxWidthClass="max-w-2xl"
      >
        <CashDeploymentSettingPanel
          mode={cashDeploymentMode}
          onSaved={handleCashDeploymentSaved}
          onCancel={() => setCashDeploymentModalOpen(false)}
          onShowExplainer={() => setCashDeploymentExplainerOpen(true)}
          appearance="alpha"
        />
      </AlphaSettingsModal>

      <CashDeploymentExplainerModal
        isOpen={cashDeploymentExplainerOpen}
        onClose={() => setCashDeploymentExplainerOpen(false)}
        appearance="alpha"
      />

      <AutomatedRebalancingExplainerModal
        isOpen={rebalancingExplainerOpen}
        onClose={() => setRebalancingExplainerOpen(false)}
        appearance="alpha"
      />

      <ContractExclusionsExplainerModal
        isOpen={exclusionsExplainerOpen}
        onClose={() => setExclusionsExplainerOpen(false)}
        appearance="alpha"
      />

      <ConfirmationModal
        isOpen={tradingConfirmOpen}
        onClose={() => {
          setTradingConfirmOpen(false);
          setTradingError(null);
        }}
        onConfirm={handleTradingToggle}
        title={userTradingEnabled ? "Pause Automated Rebalancing" : "Resume Automated Rebalancing"}
        message={
          userTradingEnabled
            ? "This stops new automated rebalancing orders for your account. Existing positions remain unchanged."
            : "This re-enables automated rebalancing for your account when system trading is available."
        }
        confirmText={userTradingEnabled ? "Pause" : "Resume"}
        isDestructive={userTradingEnabled}
        loading={tradingLoading}
        appearance="alpha"
      />
    </main>
  );
}

export type { AlphaProfilePageProps } from "./alphaPageSubject";

export default function AlphaProfilePage(
  props: AlphaProfilePageProps
): React.ReactElement {
  return props.mode === "admin-target"
    ? <AdminTargetProfileAdapter {...props} />
    : <SelfProfileAdapter {...props} />;
}
